import type { SerializedSocialChannel } from '../../../../types';

/**
 * How a post of a channel goes out (QA finding F02, decision D5). `auto`: the channel is connected
 * and signed in, so Music24 posts it at its time. `manual`: the channel has no working login, so
 * the post comes to the artist as a "post it yourself" reminder at its time. The server already
 * works this way; these rules only let the screens say it.
 */
export type PostingMode = 'auto' | 'manual';

type PostingModeFields = Pick<SerializedSocialChannel, 'status' | 'syncStatus'> &
  Partial<Pick<SerializedSocialChannel, 'publishesByApi' | 'autoPublishAvailable'>>;

/**
 * The posting mode of one channel row. The server says it (`publishesByApi`: it picks the API
 * publisher and the platform did not refuse the login). An older API does not send it; then the
 * client rule applies: connected, the login not refused, the platform set up on the server.
 * `needsReconnect` does not decide it: a connected channel that lacks only the read scopes (a
 * login from before the read side) still publishes by itself.
 */
export function postingModeOf(channel: PostingModeFields): PostingMode {
  if (channel.publishesByApi !== undefined) {
    return channel.publishesByApi ? 'auto' : 'manual';
  }
  const signedIn = channel.status === 'connected' && channel.syncStatus !== 'reauthRequired';
  return signedIn && channel.autoPublishAvailable !== false ? 'auto' : 'manual';
}

/** The platforms whose posts go out by themselves. */
export function autoPostPlatforms(channels: readonly SerializedSocialChannel[]): Set<string> {
  return new Set(channels.filter((channel) => postingModeOf(channel) === 'auto').map((channel) => channel.platform));
}

/** The channels split by their posting mode, in the order of the list. */
export function channelsByPostingMode(channels: readonly SerializedSocialChannel[]): {
  auto: SerializedSocialChannel[];
  manual: SerializedSocialChannel[];
} {
  return {
    auto: channels.filter((channel) => postingModeOf(channel) === 'auto'),
    manual: channels.filter((channel) => postingModeOf(channel) === 'manual'),
  };
}

/** How many posts go out by themselves and how many come as "post it yourself" reminders. */
export interface PostingSplit {
  auto: number;
  manual: number;
}

/**
 * The split of some posts by the platform of each. A post whose platform is not known counts as a
 * reminder: the screen never says that a post goes out by itself when it cannot know. Pure.
 */
export function postingSplitOf(
  postUuids: readonly string[],
  platformOfPost: ReadonlyMap<string, string>,
  autoPlatforms: ReadonlySet<string>
): PostingSplit {
  let auto = 0;
  postUuids.forEach((uuid) => {
    const platform = platformOfPost.get(uuid);
    if (platform !== undefined && autoPlatforms.has(platform)) {
      auto += 1;
    }
  });
  return { auto, manual: postUuids.length - auto };
}
